// ── "SAVE CONVERSATION" — a Home conversation filed under an Ongoing (busy-my-web#654) ──────────
//
// Pouria, 2026-10-21: "like research pack that we can save to any ongoing we should be able to do
// it for the chats as well". So the card that asks WHERE is the Research Pack save card's twin
// (components/SmartBox/SavePackCard.vue): the same tinted card, the same two-door chooser — an
// existing destination, or a new one named in a field — the same ‹ back to the choices, and the
// same receipt in the box's one notification slot. What differs is what the doors open onto: a pack
// card chooses among ONE story's packs, this one among the user's Ongoings, so "Choose from
// Ongoings" lists them under the field (filtered by what is typed, the Ongoing picker's own
// matching — lib/ongoingPicker's `ongoingPickerRows`, never a second ranking).
//
// Naming rule (Pouria, same day): "one chat can include many questions — let's name it
// conversation". Every word the user reads says Conversation; code keeps the `chat` identifiers.
//
// Pure: no store, no router. The store half is store/modules/chatSessions.js (`promptSave`,
// `saveToOngoing`); the card is components/SmartBox/SaveConversationCard.vue.

import { ongoingPickerRows } from '@/lib/ongoingPicker'
import { LIVE_ROW_ID } from '@/lib/chatRail'

export const SAVE_CONVERSATION_TEXT = {
  action: 'Save conversation',
  // The chooser's heading, and the field-only view's two headings once a door is chosen — the
  // pack card's own pattern ("Choose from Research Packs" / "Research pack name").
  title: 'Save conversation',
  choose: 'Choose from Ongoings',
  create: 'Create new Ongoing',
  nameHeading: 'Ongoing name',
  choosePlaceholder: 'Search for an Ongoing to save to...',
  createPlaceholder: 'Name the new Ongoing',
  save: 'Save',
  close: 'Close',
  back: 'Back to the choices',
  none: 'You have no ongoings yet.',
  empty: 'No ongoing matches that.',
  failed: 'Could not save this conversation.',
  open: 'Open',
  untitled: 'Conversation',
}

/** The two doors, in the pack card's order: an existing destination first, then a new one. */
export const SAVE_CONVERSATION_OPTIONS = [
  { key: 'existing', label: SAVE_CONVERSATION_TEXT.choose },
  { key: 'new', label: SAVE_CONVERSATION_TEXT.create },
]

/** How many Ongoings the chooser lists under its field. */
export const PICKER_LIMIT = 8

/** What the BFF answers in incognito for "a session that was not created". */
const EMPTY_GUID = '00000000-0000-0000-0000-000000000000'

/**
 * May this rail row / open conversation be saved at all? Signed in, not incognito, and a row that
 * stands for a conversation: the "Ready when you are" placeholder and a row this tab invented
 * (`local-…`, a guest's archive) have no server row to move. The live row is allowed under its
 * placeholder id — `resolveSessionId` finds its real id when it is saved.
 */
export function canSaveConversation({ authenticated = false, incognito = false, id = null, placeholder = false } = {}) {
  if (!authenticated || incognito || placeholder) return false
  const key = String(id || '')
  if (!key || key === 'pending' || key === EMPTY_GUID || key.startsWith('local-')) return false
  return true
}

/** True when the id names the live conversation without its server id (`chatRail`'s LIVE_ROW_ID). */
export const isLivePlaceholder = (id) => String(id || '') === LIVE_ROW_ID

/** The new Ongoing's suggested name: the conversation's own title, as the pack card prefills the search. */
export function defaultOngoingName(title) {
  return String(title || '').replace(/\s+/g, ' ').trim()
}

/** The card's heading for the state it is in. */
export function saveCardHeading(choice) {
  if (choice === 'existing') return SAVE_CONVERSATION_TEXT.choose
  if (choice === 'new') return SAVE_CONVERSATION_TEXT.nameHeading
  return SAVE_CONVERSATION_TEXT.title
}

/** The Ongoings the chooser lists for what is typed — the picker's rows, glyph and all. */
export function conversationOngoingRows(ongoings, query = '', now = new Date(), limit = PICKER_LIMIT) {
  return ongoingPickerRows((ongoings || []).filter(o => o && !o.isSuggested), query, now, limit)
}

/**
 * Where Save sends the conversation, or null while that is not decided yet.
 *
 *   • 'existing' — the Ongoing that was clicked; with none clicked, an Ongoing whose title is EXACTLY
 *     what was typed (the pack card's rule: typing an existing name picks it rather than making a twin);
 *   • 'new'      — a new Ongoing named by the field, unless that name is already an Ongoing's, which
 *     is then the destination for the same reason.
 */
export function saveConversationDestination({ choice = null, pickedId = null, name = '', ongoings = [] } = {}) {
  const typed = defaultOngoingName(name)
  const list = (ongoings || []).filter(o => o && o.id && !o.isArchived)
  const byName = typed ? list.find(o => String(o.title || '').trim().toLowerCase() === typed.toLowerCase()) : null
  if (choice === 'existing') {
    const picked = pickedId ? list.find(o => String(o.id) === String(pickedId)) : byName
    return picked ? { kind: 'existing', id: picked.id, title: String(picked.title || '').trim() || SAVE_CONVERSATION_TEXT.untitled } : null
  }
  if (choice === 'new') {
    if (!typed) return null
    if (byName) return { kind: 'existing', id: byName.id, title: String(byName.title || '').trim() }
    return { kind: 'new', id: null, title: typed }
  }
  return null
}

/** The receipt: "Conversation saved to <Ongoing>". */
export function savedConversationText(ongoingTitle) {
  const name = String(ongoingTitle || '').trim()
  return name ? `Conversation saved to ${name}` : 'Conversation saved'
}

/** Where the receipt's Open goes. */
export const ongoingRoute = (id) => (id ? `/ongoing/${id}` : null)
